import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { SessionOverviewResponse } from "@cabal/shared/domain/session/types.js";
import { LogLevel, useSeqLog } from "@/hooks/telemetry";
import {
  SessionUpdateSource,
  getHaltReason,
  isInsufficientTokensReason,
} from "./session-data-provider-utils";

type SessionProviderLogger = ReturnType<typeof useSeqLog>;

interface UseSessionOverviewStateInput {
  haltStatusDebugLog: SessionProviderLogger;
  showInsufficientTokensModal: () => Promise<void>;
}

interface SessionOverviewState {
  session: SessionOverviewResponse | null;
  applySessionFromSource: (input: {
    nextSession: SessionOverviewResponse;
    source: SessionUpdateSource;
  }) => void;
  applySessionLocalPatch: (input: {
    patch: Partial<SessionOverviewResponse>;
  }) => void;
}

interface SessionSourceUpdate {
  nextSession: SessionOverviewResponse;
  source: SessionUpdateSource;
}

interface ApplySessionFromSourceInput {
  haltStatusDebugLog: SessionProviderLogger;
  sessionRef: MutableRefObject<SessionOverviewResponse | null>;
  setSession: Dispatch<SetStateAction<SessionOverviewResponse | null>>;
  showInsufficientTokensModal: () => Promise<void>;
}

function shouldRaiseInsufficientTokensModal(input: {
  nextHaltReason: ReturnType<typeof getHaltReason>;
  previousHaltReason: ReturnType<typeof getHaltReason>;
  source: SessionUpdateSource;
}): boolean {
  return (
    input.source === SessionUpdateSource.Overview &&
    !isInsufficientTokensReason({ haltReason: input.previousHaltReason }) &&
    isInsufficientTokensReason({ haltReason: input.nextHaltReason })
  );
}

function logSessionOverviewUpdate(input: {
  haltStatusDebugLog: SessionProviderLogger;
  nextHaltReason: ReturnType<typeof getHaltReason>;
  nextSession: SessionOverviewResponse;
  previousHaltReason: ReturnType<typeof getHaltReason>;
  previousSession: SessionOverviewResponse | null;
  source: SessionUpdateSource;
}): void {
  input.haltStatusDebugLog(
    "apply-session: prev → next",
    {
      source: input.source,
      prevStatus: input.previousSession?.status ?? null,
      nextStatus: input.nextSession.status,
      prevHaltReason: input.previousHaltReason,
      nextHaltReason: input.nextHaltReason,
    },
    LogLevel.Info,
  );
}

export function useSessionOverviewState(
  input: UseSessionOverviewStateInput,
): SessionOverviewState {
  const { haltStatusDebugLog, showInsufficientTokensModal } = input;
  const [session, setSession] = useState<SessionOverviewResponse | null>(null);
  const sessionRef = useSyncedSessionOverviewRef(session);
  const applySessionFromSource = useApplySessionFromSource({
    haltStatusDebugLog,
    sessionRef,
    setSession,
    showInsufficientTokensModal,
  });
  const applySessionLocalPatch = useApplySessionLocalPatch({
    applySessionFromSource,
    sessionRef,
  });

  return useMemo(
    () => ({
      session,
      applySessionFromSource,
      applySessionLocalPatch,
    }),
    [session, applySessionFromSource, applySessionLocalPatch],
  );
}

function useSyncedSessionOverviewRef(
  session: SessionOverviewResponse | null,
): MutableRefObject<SessionOverviewResponse | null> {
  const sessionRef = useRef<SessionOverviewResponse | null>(null);
  useLayoutEffect(() => {
    sessionRef.current = session;
  }, [session]);

  return sessionRef;
}

function useApplySessionFromSource({
  haltStatusDebugLog,
  sessionRef,
  setSession,
  showInsufficientTokensModal,
}: ApplySessionFromSourceInput): SessionOverviewState["applySessionFromSource"] {
  return useCallback(
    (update: SessionSourceUpdate): void => {
      const previousSession = sessionRef.current;
      const previousHaltReason = getHaltReason({
        session: previousSession,
      });
      const nextHaltReason = getHaltReason({
        session: update.nextSession,
      });
      const shouldRaiseModal = shouldRaiseInsufficientTokensModal({
        nextHaltReason,
        previousHaltReason,
        source: update.source,
      });

      logSessionOverviewUpdate({
        haltStatusDebugLog,
        nextHaltReason,
        nextSession: update.nextSession,
        previousHaltReason,
        previousSession,
        source: update.source,
      });

      sessionRef.current = update.nextSession;
      setSession(update.nextSession);

      if (shouldRaiseModal) {
        void showInsufficientTokensModal();
      }
    },
    [haltStatusDebugLog, sessionRef, setSession, showInsufficientTokensModal],
  );
}

function useApplySessionLocalPatch(input: {
  applySessionFromSource: SessionOverviewState["applySessionFromSource"];
  sessionRef: MutableRefObject<SessionOverviewResponse | null>;
}): SessionOverviewState["applySessionLocalPatch"] {
  const { applySessionFromSource, sessionRef } = input;

  return useCallback(
    (patchInput: { patch: Partial<SessionOverviewResponse> }): void => {
      const currentSession = sessionRef.current;
      if (!currentSession) {
        return;
      }

      applySessionFromSource({
        nextSession: { ...currentSession, ...patchInput.patch },
        source: SessionUpdateSource.LocalPatch,
      });
    },
    [applySessionFromSource, sessionRef],
  );
}
